const createTimeReportThermal = (data, language, settings) => {
  // Ensure returns data exists
  const { hourly_analysis, summary } = data;
  const returns = data.returns || { total_amount: 0, count: 0, percentage: 0 };
  
  // Check if returns data exists and has count property
  const hasReturns = returns && (
    (typeof returns.count !== 'undefined' && returns.count > 0) || 
    (typeof returns.total_amount !== 'undefined' && returns.total_amount > 0)
  );
  
  // Format date in Gregorian format (DD/MM/YYYY)
  const currentDate = new Date();
  const day = String(currentDate.getDate()).padStart(2, '0');
  const month = String(currentDate.getMonth() + 1).padStart(2, '0');
  const year = currentDate.getFullYear();
  const hours = String(currentDate.getHours()).padStart(2, '0');
  const minutes = String(currentDate.getMinutes()).padStart(2, '0');
  
  const date = `${day}/${month}/${year}`;
  const time = `${hours}:${minutes}`;

  // Get paper dimensions from settings
  const { printing = {}, restaurant = {} } = settings || {};
  const paperWidth = printing?.paper_size || '80mm';
  const paperHeight = printing?.paper_height || 'auto';
  const fontSize = printing?.font_size || '8';

  // Calculate dimensions with space for text
  const pageWidth = parseInt(paperWidth);
  const contentWidth = pageWidth - 12; // Reduce margin to prevent right side cutoff
  
  // Parse height properly for CSS
  const parsedHeight = paperHeight === 'auto' ? 300 : parseInt(paperHeight.replace('mm', ''));

  // Format hour function - maintains 12-hour format but ensures Gregorian representation
  const formatHour = (hour) => {
    // Always use 12-hour format with AM/PM
    const displayHour = hour % 12 === 0 ? 12 : hour % 12;
    
    if (language === 'ar') {
      // Convert to Arabic numerals but keep Gregorian time format
      const toArabicNumeral = (num) => num.toString().replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);
      const period = hour >= 12 ? 'م' : 'ص';
      return `${toArabicNumeral(displayHour)} ${period}`;
    } else {
      // English format
      const period = hour >= 12 ? 'PM' : 'AM';
      return `${displayHour} ${period}`;
    }
  };

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <style>
        @page {
          margin: 0;
          size: ${pageWidth}mm ${parsedHeight}mm;
        }
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        body {
          font-family: 'Arial', sans-serif;
          font-size: ${fontSize}pt;
          width: ${contentWidth}mm;
          margin: 0 auto;
          padding: 2mm;
          direction: ${language === 'ar' ? 'rtl' : 'ltr'};
        }
        .header {
          text-align: center;
          margin-bottom: 3mm;
        }
        .title {
          font-size: ${parseInt(fontSize) + 2}pt;
          font-weight: bold;
          margin-bottom: 1mm;
        }
        .subtitle {
          font-size: ${fontSize}pt;
          margin-bottom: 1mm;
        }
        .date-time {
          font-size: ${fontSize}pt;
          margin-bottom: 2mm;
        }
        .divider {
          border-top: 1px dashed #000;
          margin: 2mm 0;
        }
        .summary {
          margin-bottom: 3mm;
        }
        .summary-item {
          display: flex;
          justify-content: space-between;
          margin-bottom: 1mm;
        }
        .returns-section {
          margin: 2mm 0;
          padding: 1mm;
          border: 1px dashed #000;
        }
        .returns-title {
          font-weight: bold;
          text-align: center;
          margin-bottom: 1mm;
        }
        .hourly-table {
          width: 100%;
          border-collapse: collapse;
          margin-top: 2mm;
        }
        .hourly-table th {
          text-align: ${language === 'ar' ? 'right' : 'left'};
          padding: 1mm 0;
          border-bottom: 1px solid #000;
          font-weight: bold;
        }
        .hourly-table td {
          padding: 1mm 0;
          border-bottom: 1px dashed #ccc;
        }
        .text-right {
          text-align: right;
        }
        .text-left {
          text-align: left;
        }
        .text-center {
          text-align: center;
        }
        .footer {
          margin-top: 3mm;
          text-align: center;
          font-size: ${parseInt(fontSize) - 1}pt;
        }
        .section-title {
          font-weight: bold;
          margin: 2mm 0;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div class="title">${language === 'ar' ? restaurant?.name_ar || 'المطعم' : restaurant?.name || 'Restaurant'}</div>
        <div class="subtitle">${language === 'ar' ? 'تقرير المبيعات حسب الوقت' : 'Time Analysis Report'}</div>
        <div class="date-time">${date} ${time}</div>
      </div>
      
      <div class="divider"></div>
      
      <div class="summary">
        <div class="summary-item">
          <span>${language === 'ar' ? 'ساعة الذروة:' : 'Peak Hour:'}</span>
          <span>${formatHour(summary.peak_hour)}</span>
        </div>
        <div class="summary-item">
          <span>${language === 'ar' ? 'إجمالي الطلبات:' : 'Total Orders:'}</span>
          <span>${summary.total_orders}</span>
        </div>
        <div class="summary-item">
          <span>${language === 'ar' ? 'إجمالي المبيعات:' : 'Total Revenue:'}</span>
          <span>${parseFloat(summary.total_revenue).toFixed(2)}</span>
        </div>
      </div>
      
      ${hasReturns ? `
      <div class="returns-section">
        <div class="returns-title">${language === 'ar' ? 'ملخص المرتجعات' : 'Returns Summary'}</div>
        <div class="summary-item">
          <span>${language === 'ar' ? 'إجمالي المرتجعات:' : 'Total Returns Amount:'}</span>
          <span>${returns.total_amount.toFixed(2)}</span>
        </div>
        <div class="summary-item">
          <span>${language === 'ar' ? 'عدد الطلبات المرتجعة:' : 'Number of Returns:'}</span>
          <span>${returns.count}</span>
        </div>
        <div class="summary-item">
          <span>${language === 'ar' ? 'نسبة المرتجعات:' : 'Returns Percentage:'}</span>
          <span>${returns.percentage.toFixed(2)}%</span>
        </div>
      </div>
      ` : ''}
      
      <div class="divider"></div>
      
      <div class="section-title">
        ${language === 'ar' ? 'تحليل المبيعات حسب الساعة' : 'Hourly Sales Analysis'}
      </div>
      
      <div class="hourly-analysis-section">
        <table class="hourly-table">
          <thead>
            <tr>
              <th style="width: 20%;">${language === 'ar' ? 'الساعة' : 'Hour'}</th>
              <th style="width: 20%;">${language === 'ar' ? 'الطلبات' : 'Orders'}</th>
              <th style="width: 20%;">${language === 'ar' ? 'المتوسط' : 'Avg'}</th>
              <th style="width: 40%;">${language === 'ar' ? 'المبيعات' : 'Revenue'}</th>
            </tr>
          </thead>
          <tbody>
            ${hourly_analysis.map(hour => `
              <tr>
                <td>${formatHour(hour.hour)}</td>
                <td>${hour.order_count}</td>
                <td>${parseFloat(hour.avg_order_value).toFixed(2)}</td>
                <td>${parseFloat(hour.total_revenue).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>
      
      <div class="divider"></div>
      
      <div class="footer">
        ${language === 'ar' ? 'شكراً لكم' : 'Thank You'}
      </div>
    </body>
    </html>
  `;
};

export default createTimeReportThermal;
